/** Limite de erro: a falha para aqui, em vez de levar a tela junto.
 *
 * Uma exceção durante o render desmonta a árvore inteira, e o React deixa a
 * página **em branco** — sem mensagem, sem navegação, sem caminho de volta.
 * Aconteceu neste produto quando o painel da oportunidade leu `stage.name` de
 * uma etapa ausente: um campo faltando apagou o CRM inteiro da tela.
 *
 * O limite converte isso no terceiro estado obrigatório do `DESIGN.md § 7`: o
 * que falhou, o que fazer, e um botão de tentar de novo. Cada região do
 * produto embrulha o seu — quanto mais perto da causa, menos a pessoa perde
 * quando algo quebra.
 *
 * Continua sendo uma classe porque **não existe equivalente em hook**:
 * `getDerivedStateFromError` e `componentDidCatch` são os únicos ganchos de
 * captura de render que o React oferece, e os dois são de classe.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

import { Botao } from "./Botao";

interface Props {
  /** O que parou, na voz de quem lê a tela: "O funil", "O histórico".
   *
   * Vai para a mensagem e para o log. "Algo deu errado" não é acionável nem
   * para quem usa nem para quem investiga; o nome da região é as duas coisas.
   */
  area: string;
  children: ReactNode;
  /** Ação extra, para quando sair da região é melhor que insistir nela —
   * fechar um painel, voltar ao funil. */
  acaoSecundaria?: { rotulo: string; aoAcionar: () => void };
}

interface Estado {
  erro: Error | null;
}

export class LimiteDeErro extends Component<Props, Estado> {
  override state: Estado = { erro: null };

  static getDerivedStateFromError(erro: Error): Estado {
    return { erro };
  }

  override componentDidCatch(erro: Error, informacao: ErrorInfo): void {
    // Erro capturado que não é registrado é erro que ninguém investiga: a
    // pessoa clica em "tentar de novo", funciona, e a causa continua lá. O
    // prefixo é o que torna a linha achável no console de um navegador real.
    console.error(`[limite-de-erro] ${this.props.area}`, erro, informacao);
  }

  private readonly tentarDeNovo = (): void => {
    this.setState({ erro: null });
  };

  override render(): ReactNode {
    const { erro } = this.state;
    if (erro === null) return this.props.children;

    const { area, acaoSecundaria } = this.props;

    return (
      <div
        role="alert"
        className="flex flex-col gap-4 rounded-lg border border-carmim/40 bg-carmim/5 p-6"
      >
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-title text-texto">{area} não carregou</h2>
          <p className="font-texto text-body text-texto-suave">
            Uma falha inesperada interrompeu esta parte da tela. O resto do produto continua
            funcionando.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Botao onClick={this.tentarDeNovo}>Tentar de novo</Botao>
          {acaoSecundaria !== undefined && (
            <Botao variante="secundario" onClick={acaoSecundaria.aoAcionar}>
              {acaoSecundaria.rotulo}
            </Botao>
          )}
        </div>

        {/* Fechado por padrão. A mensagem da exceção é para quem vai
            investigar, e quem está vendendo não tem o que fazer com ela — mas
            ela precisa existir, ou relatar o problema vira "a tela deu erro". */}
        <details>
          <summary className="cursor-pointer font-texto text-caption text-texto-tenue">
            Detalhe técnico
          </summary>
          <p className="mt-2 whitespace-pre-wrap break-words font-dados text-micro text-texto-suave">
            {erro.message}
          </p>
        </details>
      </div>
    );
  }
}
